import { FormSubmission, FormTemplate } from 'types/graphql'
import React, { useCallback, useEffect, useRef } from 'react'

interface IsolatedFormRendererProps {
  formTemplate: FormTemplate
  formSubmission: FormSubmission | null
  onSubmit?: (submission: Record<string, unknown>) => void
  onError?: (error: unknown) => void
  onFormChange?: (formData: Record<string, unknown>) => void
  onValidatePinAndSignField?: (
    submissionData: Record<string, unknown>,
    pin: string,
    fieldKey: string
  ) => void
  options?: {
    readOnly?: boolean
    noAlerts?: boolean
    [key: string]: unknown
  }
  style?: React.CSSProperties
  className?: string
}

const IsolatedFormRenderer: React.FC<IsolatedFormRendererProps> = ({
  formTemplate,
  formSubmission,
  onSubmit,
  onError,
  onFormChange,
  onValidatePinAndSignField,
  options = {},
  style,
  className,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const resizeIFrameToFitContent = useCallback(() => {
    if (iframeRef.current && iframeRef.current.contentWindow.document?.body) {
      iframeRef.current.height = `${iframeRef.current.contentWindow.document.body.scrollHeight}px`
    }
  }, [])

  // Handle messages from iframe
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return // Security check
      }

      const {
        type,
        submission,
        error,
        submissionData,
        changeData,
        pin,
        fieldKey,
      } = event.data

      switch (type) {
        case 'FORM_INITIALIZED':
          resizeIFrameToFitContent()
          break
        case 'FORM_CHANGED': {
          resizeIFrameToFitContent()
          const currentSubmissionData =
            formSubmission?.submissionData as Record<string, object>
          onFormChange?.({
            ...currentSubmissionData,
            data: { ...currentSubmissionData?.data, ...changeData },
          })
          break
        }
        case 'FORM_SUBMITTED':
          onSubmit?.(submission)
          break
        case 'FORM_ERROR':
          onError?.(error)
          break
        case 'VALIDATE_PIN_AND_SIGN_FIELD_REQUESTED':
          onValidatePinAndSignField?.(submissionData, pin, fieldKey)
          break
      }
    },
    [onSubmit, onError, onFormChange, onValidatePinAndSignField]
  )

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [handleMessage])

  const initializeForm = useCallback(() => {
    if (formTemplate) {
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: 'INIT_FORM',
          data: {
            formTemplate,
            submissionData: formSubmission?.submissionData,
            options,
          },
        },
        window.location.origin
      )
    }
  }, [formTemplate, formSubmission, options])

  useEffect(() => {
    const iframe = iframeRef.current
    if (iframe) {
      iframe.addEventListener('load', initializeForm)
    }
    return () => iframe.removeEventListener('load', initializeForm)
  }, [initializeForm])

  useEffect(() => {
    console.log('formSubmission changed')
    if (iframeRef.current?.contentWindow) {
      console.log('posting message to update submission')
      iframeRef.current.contentWindow.postMessage(
        { type: 'UPDATE_SUBMISSION', data: { submission: formSubmission } },
        window.location.origin
      )
    }
  }, [formSubmission])

  // Update options when they change
  useEffect(() => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { type: 'UPDATE_OPTIONS', data: { options } },
        window.location.origin
      )
    }
  }, [options])

  useEffect(() => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { type: 'UPDATE_SUBMISSION', data: { submission: formSubmission } },
        window.location.origin
      )
    }
  }, [formSubmission])

  return (
    <iframe
      ref={iframeRef}
      src="/form-renderer.html"
      id="form-renderer-iframe"
      className={className}
      style={{
        border: 'none',
        width: '100%',
        borderRadius: '1em',
        ...style,
      }}
      title="Form Renderer"
    />
  )
}

export default IsolatedFormRenderer
